/**
 * @fileoverview Offset-pagination helpers.
 * @description The backend's paginated list endpoints answer `{ ..., data: T[], meta: { page,
 * limit, total } }`. The shared response interceptor unwraps every envelope to its `data`, and
 * keeps a list's `meta` on the response as `pageMeta`; toPaginated puts the two back together for
 * a service to return.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { PageMeta, Paginated } from '@/shared/types/http.types'
import type { AxiosResponse } from 'axios'

/** A response whose envelope carried pagination `meta`, as handleResponseSuccess leaves it. */
export type ResponseWithPageMeta<T = unknown> = AxiosResponse<T> & { pageMeta?: PageMeta }

/**
 * @description Combines an unwrapped list response with its pagination meta.
 * @param {ResponseWithPageMeta<T[]>} response - The list endpoint's response.
 * @returns {Paginated<T>} The page's items and meta (a single page covering every item when the
 * response carried no meta).
 */
export function toPaginated<T>(response: ResponseWithPageMeta<T[]>): Paginated<T> {
  const items = response.data
  return {
    items,
    meta: response.pageMeta ?? { page: 1, limit: items.length, total: items.length },
  }
}
